import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { PlusCircle } from 'lucide-react'
import { apiClient } from '../utils/api'

interface Kid {
  id: string
  display_name: string
  total_points?: number
}

const QUICK_AMOUNTS = [5, 10, 20, 50]

/**
 * Give a kid extra points for something that is not a chore. The points go
 * into the points ledger (POST /api/points/bonus), so they are spendable at
 * once, and the kid gets a notification with the reason.
 */
export const BonusPointsPanel: React.FC<{ onAwarded?: () => void }> = ({ onAwarded }) => {
  const [kids, setKids] = useState<Kid[]>([])
  const [kidId, setKidId] = useState('')
  const [points, setPoints] = useState<string>('10')
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  const loadKids = () =>
    apiClient
      .get('/api/parent/children')
      .then((res: any) => setKids(Array.isArray(res) ? res : res?.children || []))
      .catch(() => setKids([]))

  useEffect(() => {
    loadKids()
  }, [])

  const amount = Number(points)
  const valid = kidId !== '' && Number.isInteger(amount) && amount > 0 && amount <= 1000 && reason.trim() !== ''

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!valid) return
    setSaving(true)
    try {
      const res: any = await apiClient.post('/api/points/bonus', {
        user_id: kidId,
        points: amount,
        reason: reason.trim(),
      })
      toast.success(res?.message || `Added ${amount} points`)
      setReason('')
      setKidId('')
      await loadKids()
      onAwarded?.()
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Could not add points')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-lg shadow p-6" data-testid="bonus-points-panel">
      <h2 className="text-lg font-semibold flex items-center gap-2 mb-1">
        <PlusCircle className="w-5 h-5 text-green-600" />
        Add Bonus Points
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        Reward something that isn't a chore. The points can be spent right away.
      </p>

      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Child</label>
          <select
            value={kidId}
            onChange={e => setKidId(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            <option value="">Choose a child</option>
            {kids.map(k => (
              <option key={k.id} value={k.id}>
                {k.display_name}
                {typeof k.total_points === 'number' ? ` (${k.total_points} pts)` : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Points</label>
          <input
            type="number"
            min={1}
            max={1000}
            step={1}
            value={points}
            onChange={e => setPoints(e.target.value)}
            className="w-full border rounded px-3 py-2"
          />
          <div className="flex gap-2 mt-2">
            {QUICK_AMOUNTS.map(n => (
              <button
                key={n}
                type="button"
                onClick={() => setPoints(String(n))}
                className={`px-2 py-1 text-xs rounded border ${
                  amount === n ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700'
                }`}
              >
                +{n}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
          <input
            type="text"
            value={reason}
            maxLength={200}
            placeholder="Helped carry groceries"
            onChange={e => setReason(e.target.value)}
            className="w-full border rounded px-3 py-2"
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="submit"
          disabled={!valid || saving}
          className="px-4 py-2 rounded bg-green-600 text-white disabled:opacity-50"
        >
          {saving ? 'Adding...' : valid ? `Add ${amount} points` : 'Add points'}
        </button>
      </div>
    </form>
  )
}
